import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ZagIcon } from '../icon/icon';

export type RatingInputDisplayType = 'stars' | 'slider' | 'percentage';

@Component({
  selector: 'zag-rating-input',
  imports: [ZagIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatingInput),
      multi: true,
    },
  ],
  host: {
    '[class.zag-rating-input]': 'true',
    '[class.zag-rating-input--disabled]': 'disabled()',
  },
  template: `
    @switch (displayType()) {
      @case ('stars') {
        <div class="zag-rating-input__stars" role="radiogroup" [attr.aria-label]="'Rating'" (mouseleave)="onStarHoverEnd()">
          @for (star of starIndices(); track star) {
            <button
              type="button"
              class="zag-rating-input__star"
              [class.zag-rating-input__star--filled]="isStarFilled(star)"
              [class.zag-rating-input__star--hovered]="isStarHovered(star)"
              [disabled]="disabled()"
              [attr.aria-label]="star + 1 + ' of ' + scale() + ' stars'"
              [attr.aria-checked]="isStarFilled(star)"
              role="radio"
              (click)="onStarClick(star)"
              (mouseenter)="onStarHover(star)"
              (keydown)="onStarKeyDown($event, star)"
            >
              <span zagIcon="{{ isStarActive(star) ? 'star' : 'star_border' }}"></span>
            </button>
          }
        </div>
      }
      @case ('slider') {
        <div class="zag-rating-input__slider-wrapper">
          <input
            type="range"
            class="zag-rating-input__slider"
            min="0"
            max="1"
            step="0.01"
            [value]="value() ?? 0"
            [disabled]="disabled()"
            [attr.aria-label]="'Rating'"
            (input)="onSliderInput($event)"
            (change)="onBlur()"
          />
          <span class="zag-rating-input__slider-value">{{ sliderPercentage() }}%</span>
        </div>
      }
      @case ('percentage') {
        <div class="zag-rating-input__percentage-wrapper">
          <input
            type="number"
            class="zag-rating-input__percentage-field"
            min="0"
            max="100"
            step="1"
            [value]="percentageValue()"
            [disabled]="disabled()"
            [attr.aria-label]="'Rating percentage'"
            placeholder="0"
            (input)="onPercentageInput($event)"
            (blur)="onBlur()"
          />
          <span class="zag-rating-input__percentage-suffix">%</span>
        </div>
      }
    }
  `,
  styles: [
    `
      .zag-rating-input {
        display: inline-flex;
        align-items: center;
      }

      /* --- Stars --- */

      .zag-rating-input__stars {
        display: inline-flex;
        gap: 2px;
      }

      .zag-rating-input__star {
        padding: 0;
        margin: 0;
        border: none;
        background: none;
        cursor: pointer;
        color: var(--text-color-tertiary);
        font-size: 22px;
        line-height: 1;
        border-radius: var(--border-radius-xs);
        transition: color var(--transition-duration-fast) var(--transition-timing);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }

      .zag-rating-input__star--filled,
      .zag-rating-input__star--hovered {
        color: var(--color-warning, #f59e0b);
      }

      .zag-rating-input--disabled .zag-rating-input__star--filled {
        color: var(--text-color-secondary);
      }

      /* --- Slider --- */

      .zag-rating-input__slider-wrapper {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        width: 100%;
      }

      .zag-rating-input__slider {
        flex: 1;
        min-width: 80px;
        height: 6px;
        appearance: none;
        background: var(--surface-color-hover);
        border-radius: var(--border-radius-xs);
        outline: none;
        cursor: pointer;

        &::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--color-primary);
          border: 2px solid var(--surface-color-default);
          box-shadow: var(--shadow-sm);
          cursor: pointer;
        }

        &::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: var(--color-primary);
          border: 2px solid var(--surface-color-default);
          box-shadow: var(--shadow-sm);
          cursor: pointer;
        }

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }

      .zag-rating-input__slider-value {
        font-size: var(--font-size-body-desktop);
        font-variant-numeric: tabular-nums;
        color: var(--text-color-secondary);
        min-width: 3ch;
        text-align: right;
      }

      /* --- Percentage --- */

      .zag-rating-input__percentage-wrapper {
        display: flex;
        align-items: stretch;
        background-color: var(--input-background);
        border: var(--border-width-default) solid var(--input-border);
        border-radius: var(--border-radius-sm);
        overflow: hidden;
        transition:
          border-color var(--transition-duration-fast) var(--transition-timing),
          box-shadow var(--transition-duration-fast) var(--transition-timing);

        &:focus-within {
          border-color: var(--input-border-focus);
          box-shadow: var(--shadow-sm);
        }
      }

      .zag-rating-input__percentage-field {
        width: 4em;
        min-width: 0;
        padding: var(--spacing-sm) var(--spacing-md);
        border: 0;
        appearance: textfield;
        font-size: var(--font-size-body-desktop);
        line-height: var(--line-height-normal);
        color: var(--input-text);
        background: transparent;
        text-align: center;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        &:focus {
          outline: none;
        }

        &:disabled {
          cursor: not-allowed;
          background-color: var(--surface-color-disabled);
        }
      }

      .zag-rating-input__percentage-suffix {
        display: flex;
        align-self: center;
        align-items: center;
        flex-shrink: 0;
        padding-right: var(--spacing-sm);
        color: var(--text-color-secondary);
        font-size: var(--font-size-body-desktop);
        line-height: var(--line-height-normal);
        pointer-events: none;
        user-select: none;
      }

      .zag-rating-input--disabled .zag-rating-input__percentage-wrapper {
        background-color: var(--surface-color-disabled);
      }
    `,
  ],
})
export class RatingInput implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly displayType = input<RatingInputDisplayType>('stars');
  readonly scale = input(5);
  readonly disabled = input(false);
  readonly value = model<number | null>(null);

  private readonly hoveredStar = signal<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: number | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  protected readonly starIndices = computed(() => Array.from({ length: this.scale() }, (_, i) => i));

  protected readonly sliderPercentage = computed(() => Math.round((this.value() ?? 0) * 100));

  protected readonly percentageValue = computed(() => {
    const v = this.value();
    return v === null ? '' : String(Math.round(v * 100));
  });

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.cdr.markForCheck();
      }
    });
  }

  writeValue(value: number | null): void {
    this.value.set(value === null ? null : clamp01(value));
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  // --- Stars ---

  protected isStarFilled(index: number): boolean {
    const v = this.value();
    if (v === null) return false;
    return index < Math.round(v * this.scale());
  }

  protected isStarHovered(index: number): boolean {
    const hovered = this.hoveredStar();
    if (hovered === null) return false;
    return index <= hovered;
  }

  protected isStarActive(index: number): boolean {
    return this.isStarHovered(index) || this.isStarFilled(index);
  }

  protected onStarClick(index: number): void {
    const normalized = (index + 1) / this.scale();
    this.updateValue(normalized);
  }

  protected onStarHover(index: number): void {
    if (!this.disabled()) {
      this.hoveredStar.set(index);
    }
  }

  protected onStarHoverEnd(): void {
    this.hoveredStar.set(null);
  }

  protected onStarKeyDown(event: KeyboardEvent, index: number): void {
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.min(index + 1, this.scale() - 1);
      this.onStarClick(next);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      const prev = Math.max(index - 1, 0);
      this.onStarClick(prev);
    }
  }

  // --- Slider ---

  protected onSliderInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const raw = Number(target.value);
    if (!Number.isNaN(raw)) {
      this.updateValue(clamp01(raw));
    }
  }

  // --- Percentage ---

  protected onPercentageInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const rawValue = target.value;
    if (rawValue === '') {
      this.updateValue(null);
      return;
    }

    const pct = Number(rawValue);
    if (Number.isNaN(pct)) return;

    const clamped = Math.min(100, Math.max(0, pct));
    this.updateValue(clamped / 100);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  private updateValue(value: number | null): void {
    this.value.set(value);
    this.onChange(value);
  }
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}
